"use client";

import {
  createContext,
  useContext,
  useState,
  useTransition,
  forwardRef,
  InputHTMLAttributes,
  useEffect,
} from "react";
import { useSearchParams } from "react-router-dom";

import { InputWithIcon } from "~/components/input";
import { cn } from "~/utils";

import type { ClusterType } from "./components/FilterToggle";

const QueryInput = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement>
>(({ disabled, className, ...props }, ref) => {
  const { queryParams, setQueryParams } = useQueryContext();

  const [query, setQuery] = useState(queryParams.query);

  // Sync query state with URL params when they change.
  // Only update if values differ to prevent unnecessary re-renders and placeholder flickering.
  useEffect(() => {
    if (queryParams.query !== query) setQuery(queryParams.query);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only want to run on queryParams.query change
  }, [queryParams.query]);

  useEffect(() => {
    setQueryParams({ query });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only want to run on query change
  }, [query]);

  return (
    <InputWithIcon
      placeholder="Filter clusters..."
      value={query}
      onChange={(e) => setQuery(e.target.value)}
      // Same height and border as the toolbar's other controls.
      className={cn("h-9 flex-1 border border-border-contrast", className)}
      iconName="Search"
      iconSize={16}
      iconClassName="top-2.5 text-muted-foreground"
      disabled={disabled}
      {...props}
      ref={ref}
    />
  );
});
QueryInput.displayName = "QueryInput";

export default QueryInput;

interface QueryContextType {
  queryParams: Record<string, string>;
  setQueryParams: (params: Record<string, string>) => void;
  isPending: boolean;
  startTransition: (fn: () => void) => void;
}

export const QueryContext = createContext<QueryContextType | undefined>(
  undefined,
);

export function useQueryContext() {
  const context = useContext(QueryContext);

  if (context === undefined) {
    throw new Error("useQueryContext must be used within a QueryProvider");
  }

  return context;
}

const CursorDirection = {
  Before: "before",
  After: "after",
};

type CursorDirectionType =
  (typeof CursorDirection)[keyof typeof CursorDirection];

// The unfiltered view groups by kind so BYOC's size is obvious; BYOC alone
// groups by customer; a single-kind tab starts ungrouped.
export function defaultGroupByFor(clusterType: ClusterType): string {
  if (clusterType === "all") return "kind";
  return clusterType === "byoc" ? "customer" : "none";
}

// The tabs used to be named after who owns the clusters; keep old links working.
const LEGACY_TYPES: Record<string, ClusterType> = { tpuf: "mt", saas: "st" };

export function useQueryParams() {
  const [isPending, startTransition] = useTransition();

  const [searchParams, setSearchParams] = useSearchParams();

  const rawClusterType = searchParams.get("clusterType") ?? "";
  const clusterType =
    LEGACY_TYPES[rawClusterType] ?? ((rawClusterType || "all") as ClusterType);
  const defaultGroupBy = defaultGroupByFor(clusterType);

  const queryParams = {
    region: searchParams.get("region") || "",
    query: searchParams.get("query") || "",
    cursor: searchParams.get("cursor") || "",
    cursorDirection:
      (searchParams.get("cursorDirection") as CursorDirectionType) ||
      CursorDirection.After,
    clusterType,
    // Grouping lives in the URL so the chosen view is shareable and survives reloads.
    groupBy: searchParams.get("groupBy") || defaultGroupBy,
  };

  const setQueryParams = (newParams: Partial<typeof queryParams>) => {
    const updatedParams = new URLSearchParams(searchParams);

    Object.entries(newParams).forEach(([key, value]) => {
      // A param at its default never lives in the URL.
      const isDefaultGroupBy = key === "groupBy" && value === defaultGroupBy;
      if (
        value === "" ||
        value === null ||
        value === undefined ||
        isDefaultGroupBy
      ) {
        updatedParams.delete(key);
      } else {
        updatedParams.set(key, String(value));
      }
    });

    startTransition(() => {
      setSearchParams(updatedParams);
    });
  };

  return { queryParams, setQueryParams, isPending, startTransition };
}
